import { useState, useEffect } from 'react';
import {
  loginLabPatient,
  createLabPatientAccount,
  lookupLabPatientEmail,
  getLabCheckoutSession,
  updateLabCheckoutPatientProfile,
} from '../services/labApi';
import { isValidUsPhone } from '../services/labCheckoutUtils';

/**
 * Owns patient identity / account state for lab checkout: authentication,
 * details (email/phone/dob/sex), saved payment methods, and address prefill.
 *
 * Extracted from LabCheckoutPage to keep that component focused on wizard
 * orchestration (and within the file-size budget).
 */
export default function useLabCheckoutIdentity({ clearSession }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [detailsMode, setDetailsMode] = useState('new');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [emailIdentityStatus, setEmailIdentityStatus] = useState(null);
  const [address, setAddress] = useState({ street: '', apt: '', city: '', state: '', zip: '' });
  const [addressVerified, setAddressVerified] = useState(false);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [savedPaymentMethodsLoading, setSavedPaymentMethodsLoading] = useState(false);

  const applyPatientProfile = (patient) => {
    if (!patient?.id) return false;
    setIsAuthenticated(true);
    setPatientId(patient.id);
    setPatientName(patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.email || '');
    setPatientEmail(patient.email || '');
    if (patient.email) setEmail(patient.email);
    if (patient.phone) setPhone(patient.phone);
    if (patient.dob) setDob(patient.dob);
    if (patient.sex) setSex(patient.sex);
    if (patient.address) {
      setAddress(prev => ({
        ...prev,
        street: patient.address.street || prev.street,
        apt: patient.address.apt || prev.apt,
        city: patient.address.city || prev.city,
        state: patient.address.state || prev.state,
        zip: patient.address.zip || prev.zip,
      }));
    }
    return true;
  };

  const clearAuthenticatedState = () => {
    setIsAuthenticated(false);
    setPatientId(null);
    setPatientName('');
    setPatientEmail('');
    setSavedPaymentMethods([]);
    setLoginPassword('');
    setNewPassword('');
  };

  useEffect(() => {
    const hasToken = localStorage.getItem('patient_access_token') ||
                     sessionStorage.getItem('patient_access_token') ||
                     sessionStorage.getItem('existing_patient_access_token');
    if (!hasToken) return;
    setAuthLoading(true);
    getLabCheckoutSession()
      .then(session => {
        setDetailsMode('account');
        applyPatientProfile(session.patient);
        if (session.paymentMethods) setSavedPaymentMethods(session.paymentMethods);
      })
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !loginPassword) { setAuthError('Please enter your email and password.'); return; }
    const identityResult = await lookupEmailIdentity(email, { allowExistingLogin: true });
    if (!identityResult.ok) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = await loginLabPatient(email, loginPassword);
      if (!result?.access) throw new Error('Login succeeded but session was unavailable.');
      setSavedPaymentMethodsLoading(true);
      const session = await getLabCheckoutSession().catch(() => ({ patient: null, paymentMethods: [] }));
      setSavedPaymentMethods(session.paymentMethods || []);
      setSavedPaymentMethodsLoading(false);
      setDetailsMode('account');
      if (!applyPatientProfile(session.patient)) throw new Error('Patient profile not available. Please try again.');
    } catch (err) {
      setAuthError(err.message || 'Login failed.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('patient_access_token');
    sessionStorage.removeItem('patient_access_token');
    clearAuthenticatedState();
    if (typeof clearSession === 'function') clearSession();
  };

  async function lookupEmailIdentity(value = email, { allowExistingLogin = false } = {}) {
    const normalizedEmail = String(value || '').trim().toLowerCase();
    if (!normalizedEmail) return { ok: false, message: 'Email is required.' };
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = await lookupLabPatientEmail(normalizedEmail);
      const status = result.accountStatus || (result.exists ? 'active_patient' : 'new');
      setEmailIdentityStatus(status);
      if (status === 'inactive') {
        setDetailsMode('blocked');
        const message = 'This email is already associated with an inactive account. Please contact support or use another email address.';
        setAuthError(message);
        return { ok: false, handled: true, message };
      }
      if (status === 'non_patient') {
        setDetailsMode('blocked');
        const message = 'This email is already associated with an existing account. Please contact support or use another email address.';
        setAuthError(message);
        return { ok: false, handled: true, message };
      }
      if (status === 'new' && allowExistingLogin) {
        const message = 'No account found with this email. Please check your email or select "New patient" to create an account.';
        setAuthError(message);
        return { ok: false, handled: true, message };
      }
      if (status === 'active_patient') {
        setDetailsMode('account');
        if (allowExistingLogin) {
          setAuthError('');
          return { ok: true, status };
        }
        const message = 'An account already exists for this email. Sign in under "I have an account".';
        setAuthError(message);
        return { ok: false, handled: true, message };
      }
      setAuthError('');
      return { ok: true, status };
    } catch (err) {
      const message = err.message || 'We could not check your email just now. Please try again.';
      setAuthError(message);
      return { ok: false, handled: true, message };
    } finally {
      setAuthLoading(false);
    }
  }

  const handleCreateAccountAndLogin = async () => {
    if (!email || !phone || !dob || !sex) {
      setAuthError('Complete email, phone, date of birth, and sex at birth.');
      return { ok: false, message: 'Complete email, phone, date of birth, and sex at birth.' };
    }
    if (!isValidUsPhone(phone)) {
      setAuthError('Enter a valid 10-digit phone number.');
      return { ok: false, message: 'Enter a valid phone number.' };
    }
    if (!newPassword || newPassword.length < 8) {
      setAuthError('Create a password with at least 8 characters.');
      return { ok: false, message: 'Create a password with at least 8 characters.' };
    }
    const identityResult = await lookupEmailIdentity(email);
    if (!identityResult.ok) return identityResult;
    setAuthLoading(true);
    setAuthError('');
    try {
      const onboarding = await createLabPatientAccount({ email, phone, password: newPassword, dob, sex });
      if (onboarding?.is_new_patient === false) {
        setDetailsMode('account');
        setAuthError('An account already exists for this email or phone. Sign in under "I have an account".');
        return {
          ok: false,
          handled: true,
        };
      }
      const result = await loginLabPatient(email, newPassword);
      if (!result?.access) throw new Error('Account created, but sign-in failed. Please sign in.');
      const session = await getLabCheckoutSession().catch(() => ({ patient: null, paymentMethods: [] }));
      setSavedPaymentMethods(session.paymentMethods || []);
      setDetailsMode('account');
      if (!applyPatientProfile(session.patient || result.patient)) {
        throw new Error('Patient profile not available. Please sign in.');
      }
      return { ok: true };
    } catch (err) {
      setAuthError(err.message || 'Could not create account.');
      return { ok: false, message: err.message || 'Could not create account.' };
    } finally {
      setAuthLoading(false);
    }
  };

  const savePatientDetails = async () => {
    if (!isAuthenticated || !patientId) return { ok: true };
    setAuthLoading(true);
    setAuthError('');
    try {
      const patient = await updateLabCheckoutPatientProfile({ phone, dob, sex, address });
      applyPatientProfile(patient);
      return { ok: true };
    } catch (err) {
      const message = err.message || 'Could not save patient details.';
      setAuthError(message);
      return { ok: false, message };
    } finally {
      setAuthLoading(false);
    }
  };

  return {
    isAuthenticated, patientId, patientName, patientEmail,
    detailsMode, setDetailsMode,
    email, setEmail, phone, setPhone, dob, setDob, sex, setSex,
    loginPassword, setLoginPassword, newPassword, setNewPassword,
    authLoading, authError, emailIdentityStatus,
    address, setAddress, addressVerified, setAddressVerified,
    savedPaymentMethods, setSavedPaymentMethods, savedPaymentMethodsLoading,
    handleLogin, handleLogout, handleCreateAccountAndLogin, lookupEmailIdentity, savePatientDetails,
    clearAuthenticatedState,
  };
}
